/* eslint-disable react-hooks/set-state-in-effect */
import type {
  DynamicArtworkField,
  DynamicFieldValue,
  DynamicFieldValues,
  DynamicProofingArtwork
} from './dynamicProofingFormTypes'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  filterArtworkFieldsByDependencyVisibility,
  resolveArtworkElementDependencyVisibility
} from './artworkElementDependencyVisibility'
import {
  buildDynamicProofingFormData,
  buildInitialValues,
  dynamicFieldValuesEqual,
  sanitizeDynamicFieldValues,
  sortAndFilterArtworkFields,
  validateDynamicProofingForm
} from './dynamicProofingFormUtils'

type UseDynamicProofingFormOptions = {
  artwork: DynamicProofingArtwork
  hasRenderingCoupon?: boolean
  selectedCoupon?: { renders?: boolean }
  selectedArtworkOverlayIds?: Array<number | string>
  onValuesChange?: (values: DynamicFieldValues) => void
  complete: (formData: FormData) => void
}

export const useDynamicProofingForm = ({
  artwork,
  hasRenderingCoupon: hasRenderingCouponOverride,
  selectedCoupon,
  selectedArtworkOverlayIds = [],
  onValuesChange,
  complete
}: UseDynamicProofingFormOptions) => {
  const showCouponFields =
    hasRenderingCouponOverride !== undefined ? hasRenderingCouponOverride : selectedCoupon?.renders === true
  const baseArtworkFields = useMemo(
    () => sortAndFilterArtworkFields(artwork.artwork_fields, showCouponFields),
    [artwork.artwork_fields, showCouponFields]
  )
  const [values, setValues] = useState(() => buildInitialValues(baseArtworkFields))

  const dependencyVisibility = useMemo(
    () =>
      resolveArtworkElementDependencyVisibility({
        artwork: { ...artwork, artwork_fields: baseArtworkFields },
        fieldValues: values,
        selectedArtworkOverlayIds
      }),
    [artwork, baseArtworkFields, selectedArtworkOverlayIds, values]
  )

  const sortedArtworkFields = useMemo(
    () => filterArtworkFieldsByDependencyVisibility(baseArtworkFields, dependencyVisibility),
    [baseArtworkFields, dependencyVisibility]
  )

  useEffect(() => {
    setValues(buildInitialValues(baseArtworkFields))
  }, [artwork.id, baseArtworkFields])

  useEffect(() => {
    setValues(currentValues => {
      const sanitizedValues = sanitizeDynamicFieldValues(baseArtworkFields, sortedArtworkFields, currentValues)
      return dynamicFieldValuesEqual(currentValues, sanitizedValues) ? currentValues : sanitizedValues
    })
  }, [baseArtworkFields, sortedArtworkFields])

  useEffect(() => {
    onValuesChange?.(values)
  }, [onValuesChange, values])

  const validation = useMemo(
    () => validateDynamicProofingForm(sortedArtworkFields, values, artwork.show_no_fields),
    [artwork.show_no_fields, sortedArtworkFields, values]
  )

  useEffect(() => {
    if (validation.comparisonToastMessage) {
      toast.error(validation.comparisonToastMessage, { toastId: 'comparisonError' })
    }
  }, [validation.comparisonToastMessage])

  const handleChange = (value: DynamicFieldValue, field: DynamicArtworkField) => {
    if (value === null) {
      setValues(currentValues => ({ ...currentValues, [field.name]: null }))
      return
    }

    if (field.max_length && value.toString().length > field.max_length) {
      alert(`Maximum length for this field is ${field.max_length} characters`)
      return
    }

    let textValue = value
    if (field.is_url && typeof value === 'string') {
      textValue = value.startsWith('https://') ? value : `https://${value}`
    }

    setValues(currentValues => ({
      ...currentValues,
      [field.name]: textValue
    }))
  }

  const shiftBulletValue = (previousFieldName: string, fieldName: string, value: DynamicFieldValue) => {
    setValues(currentValues => ({
      ...currentValues,
      [previousFieldName]: value,
      [fieldName]: ''
    }))
  }

  const handlePreviewProof = () => {
    complete(buildDynamicProofingFormData(sortedArtworkFields, values))
  }

  return {
    values,
    fieldErrors: validation.fieldErrors,
    errorMessage: validation.isFormValid ? '' : 'Please complete all required fields',
    isFormValid: validation.isFormValid,
    sortedArtworkFields,
    handleChange,
    handlePreviewProof,
    shiftBulletValue
  }
}
